import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Lang } from "@/i18n/languages";
import { LANGUAGES } from "@/i18n/languages";
import { useLang } from "@/hooks/useLang";
import { stripLangPrefix } from "@/utils/routeBuilders";
import { buildAbsoluteUrl } from "@/seo/domains";
import { buildLangPathname } from "@/seo/seoPaths";

type SeoPageKey =
  | "home"
  | "prices"
  | "request-quote"
  | "privacy-policy"
  | "terms-of-service"
  | "not-found";

type SeoProps =
  | { page: SeoPageKey; service?: never; canonicalPathname?: string }
  | {
      page: "service";
      service:
        | "implantology"
        | "endodontics"
        | "oral-surgery"
        | "periodontology"
        | "orthodontics"
        | "bonding"
        | "prosthetics"
        | "lip-filler"
        | "cariesology"
        | "teeth-whitening"
        | "dental-tourism";
      canonicalPathname?: string;
    };

function getSeoKey(props: SeoProps): { titleKey: string; descriptionKey: string } {
  if (props.page === "service") {
    return {
      titleKey: `pages.services.${props.service}.title`,
      descriptionKey: `pages.services.${props.service}.description`,
    };
  }
  return {
    titleKey: `pages.${props.page}.title`,
    descriptionKey: `pages.${props.page}.description`,
  };
}

export default function Seo(props: SeoProps) {
  const { lang } = useLang();
  const location = useLocation();
  const { t } = useTranslation("seo");

  const strippedPathname = stripLangPrefix(location.pathname);
  const canonicalPathname = props.canonicalPathname ?? buildLangPathname(lang, strippedPathname);

  const canonicalUrl = buildAbsoluteUrl(lang, canonicalPathname);

  const alternates = (LANGUAGES as Lang[]).map((l) => {
    const pathname = buildLangPathname(l, strippedPathname);
    return { lang: l, href: buildAbsoluteUrl(l, pathname) };
  });

  const { titleKey, descriptionKey } = getSeoKey(props);

  const title = t(titleKey);
  const description = t(descriptionKey);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {alternates.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={a.href} />
      ))}
      {/* x-default points to English domain */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={buildAbsoluteUrl("en", buildLangPathname("en", strippedPathname))}
      />
    </Helmet>
  );
}

